/**
 * §v5.two-aurora (2026-06-23) — the theme system is exactly TWO variants,
 * presented to the operator as light / dark mode:
 *
 *   • "aurora-light" — the soft pastel-rainbow Apple-Liquid-Glass LIGHT
 *                      mode.  ★ DEFAULT (applied via html.theme-aurora-light)
 *   • "aurora-dusk"  — the original Aurora Dusk DARK mode (magenta/teal on
 *                      near-black). Lives in :root; html.theme-aurora-dusk
 *                      is a no-op alias kept only as an explicit selection
 *                      target for the cascade.
 *
 * The choice is the tenant's, persisted in localStorage, and defaults to
 * aurora-light. The ONLY control is the light/dark toggle in the sidebar
 * profile card (ThemeToggle) — the floating multi-theme switcher pill and
 * the Cream / Warm-Sunset / Midnight-Ocean palettes were removed.
 *
 * Token overrides live in src/styles/themes.css. Legacy persisted values
 * ("light"/"dark"/"cream"/"warm-sunset"/"midnight-ocean") are mapped to the
 * nearest mode on read so returning sessions don't break.
 */

const THEME_KEY = "xpand.theme";

/** The only two themes. Light is the default. */
export type Theme = "aurora-light" | "aurora-dusk";

const THEME_CLASSES: Record<Theme, string> = {
  // Light mode — real token overrides in themes.css.
  "aurora-light": "theme-aurora-light",
  // Dark mode — no-op alias; Aurora Dusk is the :root default. The class
  // exists so the active-state UI + an explicit selection have a target.
  "aurora-dusk": "theme-aurora-dusk",
};

const ALL_CLASSES = Object.values(THEME_CLASSES);

/** Resolve any persisted/legacy value to one of the two live themes.
 *  Default (nothing saved, or an unknown value) is aurora-light. */
function normalize(theme: string | null): Theme {
  // Dark-family values (current + legacy) → dark.
  if (theme === "aurora-dusk" || theme === "dark" || theme === "midnight-ocean") {
    return "aurora-dusk";
  }
  // Everything else — "aurora-light", legacy light themes ("light", "cream",
  // "warm-sunset"), null, or anything unrecognised — resolves to the light
  // default.
  return "aurora-light";
}

export function getTheme(): Theme {
  if (typeof window === "undefined") return "aurora-light";
  return normalize(window.localStorage.getItem(THEME_KEY));
}

export function setTheme(next: Theme): void {
  if (typeof window === "undefined") return;
  const resolved = normalize(next);
  const html = document.documentElement;
  // Remove the legacy light class so it doesn't fight the new ones.
  html.classList.remove("platform-light");
  // Strip any prior theme class, then apply the resolved one.
  for (const cls of ALL_CLASSES) html.classList.remove(cls);
  html.classList.add(THEME_CLASSES[resolved]);
  try {
    window.localStorage.setItem(THEME_KEY, resolved);
  } catch {
    // private mode — class still applies for the session
  }
  window.dispatchEvent(new CustomEvent("xpand:theme-change", { detail: resolved }));
}

/** Flip light ↔ dark — the sidebar ThemeToggle's only action. */
export function toggleTheme(): Theme {
  const next: Theme = getTheme() === "aurora-dusk" ? "aurora-light" : "aurora-dusk";
  setTheme(next);
  return next;
}

/* =========================================================================
 * §v4.glass-intensity (2026-06-09) → §v5 (2026-06-23) — the Golden Gate
 * Liquid-Glass opacity SLIDER was removed together with the floating theme
 * switcher it lived in. Glass intensity is now FIXED at the design default
 * (1 = the see-through Aurora the palette was tuned for, pixel-identical to
 * before). The only job left here is accessibility: when the OS asks for
 * "Reduce Transparency" or "Increase Contrast", the @media rules in
 * xpand-design.css drive --glass-intensity (down to 0.08 / 0). An inline
 * style would beat a stylesheet @media block, so under those preferences we
 * REMOVE the inline value and let CSS win; we also re-apply when the OS
 * preference changes at runtime.
 * ========================================================================= */

const GLASS_DEFAULT = 1;

/** True when the OS asks us to cut transparency / raise contrast — in which
 *  case the stylesheet @media rules own --glass-intensity, not us. */
function osWantsLessGlass(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return (
    window.matchMedia("(prefers-reduced-transparency: reduce)").matches ||
    window.matchMedia("(prefers-contrast: more)").matches
  );
}

/** Pin --glass-intensity to the design default, unless the OS prefers less
 *  glass — then yield to the stylesheet @media rules by removing the inline
 *  value. */
export function applyGlassIntensity(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (osWantsLessGlass()) {
    root.style.removeProperty("--glass-intensity");
    return;
  }
  root.style.setProperty("--glass-intensity", String(GLASS_DEFAULT));
}

let glassMediaWired = false;
function wireGlassMediaListeners(): void {
  if (glassMediaWired || typeof window === "undefined" || !window.matchMedia) {
    return;
  }
  glassMediaWired = true;
  const reapply = () => applyGlassIntensity();
  for (const q of [
    "(prefers-reduced-transparency: reduce)",
    "(prefers-contrast: more)",
  ]) {
    const mq = window.matchMedia(q);
    if (mq.addEventListener) mq.addEventListener("change", reapply);
    // Safari < 14 fallback
    else if ((mq as unknown as { addListener?: (cb: () => void) => void }).addListener) {
      (mq as unknown as { addListener: (cb: () => void) => void }).addListener(reapply);
    }
  }
}

/** Apply the persisted theme on boot. Called from main.tsx so the class
 *  lands before React renders → no flash of the default. */
export function applySavedTheme(): void {
  if (typeof window === "undefined") return;
  setTheme(getTheme());
  applyGlassIntensity();
  wireGlassMediaListeners();
}

/**
 * §v3.tenant-attr (2026-05-15) — write the active tenant onto the
 * root <html> element as `data-tenant="..."`. Tenant-scoped CSS is
 * gated on it (e.g. the roze dashboard wallpaper, scoped to
 * `html[data-tenant="roze"] .xc-host-dashboard` in app-canvas.css)
 * so it only activates for that workspace and never leaks into the
 * others.
 *
 * Called twice:
 *   • main.tsx — on boot, reads the persisted tenant from localStorage
 *     so the first paint is correctly scoped.
 *   • ProfilePicker — after the operator picks a new profile, so the
 *     scoped CSS flips without a page reload.
 *
 * Tenant slug is read from auth.ts:getTenantId() (which also drives
 * the X-Tenant-ID header on every API call). Single source of truth.
 */
export function applyTenantAttr(): void {
  if (typeof document === "undefined") return;
  try {
    const tenant = localStorage.getItem("xpand.tenant_id") ?? "roze";
    document.documentElement.setAttribute("data-tenant", tenant);
  } catch {
    document.documentElement.setAttribute("data-tenant", "roze");
  }
}


/**
 * §brand-identity (2026-08-20) — apply the tenant's primary colour.
 *
 * Sets `--accent` on the root element, which the token system already threads
 * through buttons, focus rings and highlights. Setting the TOKEN rather than
 * restyling components means the colour reaches every surface that already
 * respects the theme, including ones written later.
 *
 * Called with "" (an unset brand, or a tenant switch to one) it REMOVES the
 * inline override so the stylesheet's own value takes back over. That matters:
 * leaving the previous tenant's colour on the element after a workspace switch
 * would be a visual cross-tenant leak, the same class of bug as an unset
 * field resolving to somebody else's identity.
 *
 * The value is validated server-side to #RGB/#RRGGBB before it is stored; the
 * shape check here is a second gate, because this string goes into a CSS
 * custom property and a stylesheet is not a place to trust input.
 */
const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function applyBrandPrimary(colour: string | null | undefined): void {
  const root = document.documentElement;
  const value = (colour || "").trim();
  if (!value || !HEX_COLOUR.test(value)) {
    root.style.removeProperty("--accent");
    return;
  }
  root.style.setProperty("--accent", value);
}
